import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_API_BASE_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    setSocket(newSocket);

    return () => newSocket.close();
  }, []);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};

// Theme Context for Dark/Light Mode and Color Theme
const ThemeContext = createContext();

// Color theme options
const colorThemes = {
  blue: {
    name: 'Blue',
    primary: '#3b82f6',
    primaryDark: '#1d4ed8',
    accent: '#f97316'
  },
  orange: {
    name: 'Orange',
    primary: '#f97316',
    primaryDark: '#ea580c',
    accent: '#3b82f6'
  },
  purple: {
    name: 'Purple',
    primary: '#8b5cf6',
    primaryDark: '#7c3aed',
    accent: '#f97316'
  },
  green: {
    name: 'Green',
    primary: '#10b981',
    primaryDark: '#059669',
    accent: '#3b82f6'
  },
  pink: {
    name: 'Pink',
    primary: '#ec4899',
    primaryDark: '#db2777',
    accent: '#3b82f6'
  },
  teal: {
    name: 'Teal',
    primary: '#14b8a6',
    primaryDark: '#0d9488',
    accent: '#f97316'
  },
  red: {
    name: 'Red',
    primary: '#ef4444',
    primaryDark: '#dc2626',
    accent: '#3b82f6'
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [colorTheme, setColorTheme] = useState(() => {
    const savedColorTheme = localStorage.getItem('colorTheme');
    return savedColorTheme || 'blue';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Apply color theme
  useEffect(() => {
    const root = window.document.documentElement;
    // Remove all color theme classes
    const themes = ['theme-blue', 'theme-orange', 'theme-purple', 'theme-green', 'theme-pink', 'theme-teal', 'theme-red'];
    root.classList.remove(...themes);
    // Add current color theme class
    root.classList.add(`theme-${colorTheme}`);
    localStorage.setItem('colorTheme', colorTheme);
  }, [colorTheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const cycleColorTheme = () => {
    const themes = Object.keys(colorThemes);
    const currentIndex = themes.indexOf(colorTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setColorTheme(themes[nextIndex]);
  };

  const value = {
    isDarkMode,
    toggleTheme,
    setIsDarkMode,
    colorTheme,
    setColorTheme,
    cycleColorTheme,
    colorThemes,
    currentThemeColors: colorThemes[colorTheme]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};